// Register session detail — React port of reports/PosRegisterComponent.vue.
// Loads GET pos/register/{id} (all per-gateway totals computed server-side).
// total = cash + credit card + cheque + other + gift card − refunds − expenses.

import { useEffect, useState } from 'react';
import { Alert, Box, Stack, Typography } from '@mui/material';

import { adminApi, apiErrorMessage } from '../../api/client';
import { LoadingBlock } from '../../components/StateBlock';
import { formatDateTime, t } from '../../i18n';
import { money, num, relName } from './reportUtils';

type Rec = Record<string, unknown>;

export function registerTotals(record: Rec) {
  const sales =
    num(record.total_cash_amount) +
    num(record.total_cc_slips_amount) +
    num(record.total_cheques_amount) +
    num(record.total_other_amount) +
    num(record.total_gift_card_amount);
  return { sales, total: sales - num(record.total_refunds_amount) - num(record.total_expenses_amount) };
}

function Line({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      sx={{ px: 1.5, py: 0.75, borderBottom: 1, borderColor: 'divider', bgcolor: strong ? 'action.hover' : undefined }}
    >
      <Typography variant="body2" sx={{ fontWeight: strong ? 700 : 400 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }}>
        {value}
      </Typography>
    </Stack>
  );
}

export function RegisterDetail({ row }: { row: Rec }) {
  const [record, setRecord] = useState<Rec | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    adminApi
      .get(`pos/register/${row.id}`)
      .then((r) => alive && setRecord(r.data as Rec))
      .catch((e) => {
        if (!alive) return;
        setError(apiErrorMessage(e, t('registerRecords.detailFailed')));
        setRecord(row);
      });
    return () => {
      alive = false;
    };
  }, [row]);

  if (!record) return <LoadingBlock />;
  const { sales, total } = registerTotals(record);
  // `show` does not load closedByUser; fall back to the list row's relation.
  const closedBy = relName(record.closed_by_user ?? row.closed_by_user ?? row.closedByUser, '');

  return (
    <Stack spacing={2}>
      {error ? <Alert severity="warning">{error}</Alert> : null}
      {!record.closed_at ? <Alert severity="info">{t('registerRecords.notClosed')}</Alert> : null}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        <Line label={t('registerRecords.register')} value={relName(record.register ?? row.register)} strong />
        <Line label={t('registerRecords.user')} value={relName(record.user ?? row.user)} />
        <Line label={t('registerRecords.openedAt')} value={formatDateTime(record.created_at)} />
        {record.closed_at ? <Line label={t('registerRecords.closedAt')} value={formatDateTime(record.closed_at)} /> : null}
        {record.closed_by && closedBy ? <Line label={t('registerRecords.closedBy')} value={closedBy} /> : null}
        <Line label={t('registerRecords.totalSalesAmount')} value={money(sales)} />
        <Line label={t('registerRecords.salesGrandTotal')} value={money(record.total_sales_amount ?? row.sales_sum_grand_total ?? 0)} />
      </Box>
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        <Line label={t('registerRecords.cashInHand')} value={money(record.cash_in_hand)} />
        <Line label={t('registerRecords.cashAmount')} value={money(record.total_cash_amount)} />
        <Line label={t('registerRecords.ccAmount')} value={`${money(record.total_cc_slips_amount)} (${num(record.total_cc_slips)})`} />
        <Line label={t('registerRecords.chequeAmount')} value={`${money(record.total_cheques_amount)} (${num(record.total_cheques)})`} />
        <Line label={t('registerRecords.otherAmount')} value={money(record.total_other_amount)} />
        <Line label={t('registerRecords.giftCardAmount')} value={money(record.total_gift_card_amount)} />
        <Line label={t('registerRecords.returnOrdersAmount')} value={money(record.total_return_orders_amount)} />
        <Line label={t('registerRecords.refundsAmount')} value={money(record.total_refunds_amount)} />
        <Line label={t('registerRecords.expensesAmount')} value={money(record.total_expenses_amount)} />
        <Line label={t('registerRecords.totalAmount')} value={money(total)} strong />
      </Box>
      {record.closed_at ? (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
          <Line label={t('registerRecords.cashSubmitted')} value={money(record.total_cash_submitted)} />
          <Line label={t('registerRecords.chequeSubmitted')} value={money(record.total_cheques_submitted)} />
          <Line label={t('registerRecords.ccSubmitted')} value={money(record.total_cc_slips_submitted)} />
        </Box>
      ) : null}
      {record.comment ? <Alert severity="info" icon={false}>{String(record.comment)}</Alert> : null}
    </Stack>
  );
}
